import { ChevronRight, Loader2 } from "lucide-react";
import { Link, useRouter } from "@tanstack/react-router";
import { Button, UserAvatar } from "@somethings-ui/web";
import { useAuth } from "@/components/auth/auth-context";
import type { User } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { getPrimaryRole, isMentorIdentity } from "@/lib/utils/system-roles";
import { useCallback, useState } from "react";

interface UserRecordProfileMenuProps {
  user: User;
  onClose?: () => void;
}

export function UserRecordProfileMenu({
  user,
  onClose,
}: UserRecordProfileMenuProps) {
  const { revokeSession } = useAuth();
  const router = useRouter();
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const [pendingLogout, setPendingLogout] = useState(false);
  const photo = useSignedMedia(user.profile_picture_url);
  const name = user.name || user.email || "Account";
  const roleLabel = formatRoleLabel(getPrimaryRole(user.systemRoles));
  const isMentor = isMentorIdentity(user.systemRoles);
  const profileTo = isMentor ? "/profile/matching" : "/profile/account";
  const showSettingsComingSoon = isMentor && !hasPrivilegedAccess;

  const handleLogout = useCallback(async () => {
    setPendingLogout(true);
    await revokeSession();
    setPendingLogout(false);
    onClose?.();
    router.navigate({ to: "/login" });
  }, [onClose, revokeSession, router]);

  return (
    <div className="w-64 rounded-2xl border border-surfaces-z5 bg-surfaces-z0 p-4 shadow-lg">
      <div className="flex items-center gap-3">
        <UserAvatar name={name} imageUrl={photo.url} size="md" />
        <div className="min-w-0">
          <p className="truncate font-heading text-sm font-bold text-text-primary">
            {name}
          </p>
          {roleLabel ? (
            <p className="font-body text-xs text-text-secondary">{roleLabel}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        <Link
          to={profileTo}
          onClick={() => onClose?.()}
          className="flex items-center justify-between rounded-lg px-2 py-2.5 font-body text-sm text-text-primary hover:bg-surfaces-z4"
        >
          Profile
          <ChevronRight className="size-4 text-text-secondary" />
        </Link>
        {showSettingsComingSoon ? (
          <div className="flex items-center justify-between rounded-lg px-2 py-2.5 font-body text-sm text-text-secondary">
            Settings
            <span className="rounded-full bg-surfaces-z5 px-2 py-0.5 font-body text-xs text-text-secondary">
              Coming soon
            </span>
          </div>
        ) : (
          <Link
            to="/settings"
            onClick={() => onClose?.()}
            className="flex items-center justify-between rounded-lg px-2 py-2.5 font-body text-sm text-text-primary hover:bg-surfaces-z4"
          >
            Settings
            <ChevronRight className="size-4 text-text-secondary" />
          </Link>
        )}
      </div>

      <Button
        className="mt-3"
        size="sm"
        variant="secondary"
        disabled={pendingLogout}
        onPress={() => void handleLogout()}
      >
        {pendingLogout ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          "Sign Out"
        )}
      </Button>
    </div>
  );
}
